import { createContext, useContext, useState, useCallback, type ReactNode } from 'react'
import { API_ENDPOINTS, getMemoryEndpoint, KAIROS_KOSMOS_API_URL } from '@/lib/api'
import type { LoginInput, AuthResponse, Memory, CreateMemoryInput, UpdateMemoryInput, PaginatedResponse } from '@/types/memory'
import type { Person } from '@/types/person'

interface AuthContextType {
  token: string | null
  isAuthenticated: boolean
  login: (credentials: LoginInput) => Promise<void>
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(() => localStorage.getItem('auth_token'))

  const login = useCallback(async (credentials: LoginInput) => {
    const response = await fetch(API_ENDPOINTS.AUTH_LOGIN, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(credentials),
    })
    if (!response.ok) {
      throw new Error('Invalid credentials')
    }
    const data: AuthResponse = await response.json()
    localStorage.setItem('auth_token', data.access_token)
    setToken(data.access_token)
  }, [])

  const logout = useCallback(async () => {
    if (token) {
      await fetch(API_ENDPOINTS.AUTH_LOGOUT, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      })
    }
    localStorage.removeItem('auth_token')
    setToken(null)
  }, [token])

  return (
    <AuthContext.Provider value={{ token, isAuthenticated: !!token, login, logout }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export function useApi() {
  const { token } = useAuth()

  const authHeaders = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  }

  const getMemories = async (page = 1, pageSize = 20): Promise<PaginatedResponse<Memory>> => {
    const response = await fetch(`${API_ENDPOINTS.MEMORIES}?page=${page}&page_size=${pageSize}`, { headers: authHeaders })
    if (!response.ok) throw new Error('Failed to fetch memories')
    return response.json()
  }

  const getMemory = async (id: string): Promise<Memory> => {
    const response = await fetch(getMemoryEndpoint(id), { headers: authHeaders })
    if (!response.ok) throw new Error('Failed to fetch memory')
    return response.json()
  }

  const createMemory = async (data: CreateMemoryInput): Promise<Memory> => {
    const response = await fetch(API_ENDPOINTS.MEMORIES, {
      method: 'POST',
      headers: authHeaders,
      body: JSON.stringify(data),
    })
    if (!response.ok) throw new Error('Failed to create memory')
    return response.json()
  }

  const updateMemory = async (id: string, data: UpdateMemoryInput): Promise<Memory> => {
    const response = await fetch(getMemoryEndpoint(id), {
      method: 'PUT',
      headers: authHeaders,
      body: JSON.stringify(data),
    })
    if (!response.ok) throw new Error('Failed to update memory')
    return response.json()
  }

  const deleteMemory = async (id: string): Promise<void> => {
    const response = await fetch(getMemoryEndpoint(id), {
      method: 'DELETE',
      headers: authHeaders,
    })
    if (!response.ok) throw new Error('Failed to delete memory')
  }

  const getPeople = async (): Promise<Person[]> => {
    if (!KAIROS_KOSMOS_API_URL) return []
    try {
      const response = await fetch(`${KAIROS_KOSMOS_API_URL}/api/people`, { headers: authHeaders })
      if (!response.ok) return []
      const data = await response.json()
      return data.data || []
    } catch {
      return []
    }
  }

  return { getMemories, getMemory, createMemory, updateMemory, deleteMemory, getPeople }
}